// This is a COPY of downread/shared/src/utils/feed-url-safety.ts. To modify, edit the original and then run downread/bin/build-shared.sh
/**
 * Security rules for which URLs may be added as feeds. Enforced identically on the
 * frontend (AddFeed, to block before any request), the API (requestFeed /
 * subscribeWithMode handlers) and the sync-server auto-add job.
 *
 * Rejected, for security reasons (SSRF / abuse):
 * - IP-address hosts (IPv4, IPv6, and decimal/hex integer forms)
 * - localhost, *.localhost and *.local hosts
 * - non-standard ports (only the implicit 80/443 plus 8000 and 8443 are allowed)
 * - Downread's own domains (downread.com and subdomains), except the hosts in
 *   ALLOWED_DOWNREAD_HOSTS which serve public test feeds
 * - anything that isn't a parseable http(s) URL
 */

export type FeedUrlRejection = 'ip' | 'local' | 'port' | 'downread' | 'invalid';

// Non-default ports we allow in addition to the implicit http (80) / https (443).
const ALLOWED_EXTRA_PORTS = new Set(['8000', '8443']);

// Downread-owned hosts that are exempt from the downread.com block because they only
// serve static, public test feeds (no internal services reachable via SSRF).
const ALLOWED_DOWNREAD_HOSTS = new Set(['test-rss.downread.com']);

function isIpLikeHost(host: string): boolean {
    if (host.startsWith('[')) return true;                 // IPv6 literal (URL wraps it in brackets)
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return true; // dotted IPv4
    if (/^\d+$/.test(host)) return true;                   // decimal integer IP (e.g. 2130706433)
    if (/^0x[0-9a-f]+$/i.test(host)) return true;          // hex IP (e.g. 0x7f000001)
    return false;
}

/**
 * Returns the reason a feed URL is not allowed, or undefined when it is acceptable.
 */
export function checkFeedUrlSafety(url: string | undefined): FeedUrlRejection | undefined {
    if (!url) return 'invalid';

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return 'invalid';
    }

    if (parsed.protocol != 'http:' && parsed.protocol != 'https:')
        return 'invalid';

    const host = parsed.hostname.toLowerCase();
    if (!host) return 'invalid';

    if (host == 'localhost' || host.endsWith('.localhost') || host.endsWith('.local'))
        return 'local';

    if (isIpLikeHost(host))
        return 'ip';

    if ((host == 'downread.com' || host.endsWith('.downread.com')) && !ALLOWED_DOWNREAD_HOSTS.has(host))
        return 'downread';

    const port = parsed.port;
    if (port && port != '80' && port != '443' && !ALLOWED_EXTRA_PORTS.has(port))
        return 'port';

    return undefined;
}

/** User-facing message for a rejection reason. Shared so client and server wording match. */
export function feedUrlRejectionMessage(reason: FeedUrlRejection): string {
    switch (reason) {
        case 'ip':
            return 'For security reasons, feeds cannot be added by IP address. Please use a public domain name.';
        case 'local':
            return 'For security reasons, local addresses such as localhost or .local cannot be added as feeds.';
        case 'port':
            return 'For security reasons, this port is not allowed. Only standard web ports are supported.';
        case 'downread':
            return 'This URL cannot be added as a feed.';
        case 'invalid':
            return 'Please enter a valid feed URL.';
    }
}
